/** Procedural clips, so the cube has something to slice without loading a file. */

export type SyntheticPattern = 'crossing' | 'ink' | 'plasma' | 'rings';

export const SYNTHETIC_PATTERNS: Array<{ value: SyntheticPattern; label: string }> = [
  { value: 'crossing', label: 'crosswalk (figures + traffic)' },
  { value: 'ink', label: 'ink plume' },
  { value: 'plasma', label: 'plasma field' },
  { value: 'rings', label: 'expanding rings' },
];

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Walker {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  shade: number;
  accent: string | null;
}

export class SyntheticSource {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private pattern: SyntheticPattern = 'crossing';
  private walkers: Walker[] = [];
  private rng = mulberry32(1);
  private imageData: ImageData | null = null;

  constructor(
    readonly width = 512,
    readonly height = 288,
  ) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: false });
    if (!ctx) throw new Error('Could not create the synthetic 2D context');
    this.canvas = canvas;
    this.ctx = ctx;
  }

  /** Resets simulation state. Call before generating a run of frames. */
  reset(pattern: SyntheticPattern, seed = 7): void {
    this.pattern = pattern;
    this.rng = mulberry32(seed);
    this.imageData = null;

    const rng = this.rng;
    this.walkers = [];
    if (pattern === 'crossing') {
      const accents = ['#d8493f', '#e0a33c', '#3f6fd8', null, null, null, null, null];
      for (let i = 0; i < 26; i++) {
        const leftToRight = rng() > 0.5;
        this.walkers.push({
          x: leftToRight ? -rng() * 0.4 : 1 + rng() * 0.4,
          y: 0.18 + rng() * 0.64,
          vx: (leftToRight ? 1 : -1) * (0.0028 + rng() * 0.0032),
          vy: (rng() - 0.5) * 0.0011,
          size: 0.016 + rng() * 0.012,
          shade: 0.1 + rng() * 0.28,
          accent: accents[Math.floor(rng() * accents.length)],
        });
      }
    } else if (pattern === 'rings') {
      for (let i = 0; i < 7; i++) {
        this.walkers.push({
          x: 0.15 + rng() * 0.7,
          y: 0.15 + rng() * 0.7,
          vx: 0,
          vy: 0,
          size: rng() * 120,
          shade: rng(),
          accent: null,
        });
      }
    }
  }

  /** Renders frame `index` of `total` into the internal canvas. */
  render(index: number, total: number): HTMLCanvasElement {
    const t = total > 1 ? index / (total - 1) : 0;
    switch (this.pattern) {
      case 'crossing':
        this.renderCrossing(index, t);
        break;
      case 'ink':
        this.renderInk(index, t);
        break;
      case 'plasma':
        this.renderPlasma(index, t);
        break;
      case 'rings':
        this.renderRings(index, t);
        break;
    }
    return this.canvas;
  }

  private renderCrossing(index: number, t: number): void {
    const { ctx, width: w, height: h } = this;

    const light = ctx.createLinearGradient(0, 0, w, h);
    light.addColorStop(0, '#9a9a97');
    light.addColorStop(0.55, '#87878a');
    light.addColorStop(1, '#6f7075');
    ctx.fillStyle = light;
    ctx.fillRect(0, 0, w, h);

    // Crosswalk stripes, drawn in perspective-ish skew.
    ctx.save();
    ctx.translate(w * 0.5, h * 0.5);
    ctx.transform(1, 0.08, -0.22, 1, 0, 0);
    ctx.translate(-w * 0.5, -h * 0.5);
    ctx.fillStyle = 'rgba(238, 238, 234, 0.92)';
    const stripes = 11;
    for (let i = 0; i < stripes; i++) {
      const x = (i / stripes) * w * 1.25 - w * 0.1;
      ctx.fillRect(x, h * 0.08, w * 0.045, h * 0.86);
    }
    ctx.restore();

    // Kerb + stop line.
    ctx.fillStyle = 'rgba(62, 62, 66, 0.85)';
    ctx.fillRect(0, 0, w, h * 0.06);
    ctx.fillStyle = 'rgba(232, 232, 226, 0.7)';
    ctx.fillRect(0, h * 0.075, w, h * 0.012);

    // A bus sweeping through on a slower cycle leaves a long smear in t.
    const busCycle = (t * 1.35) % 1;
    const busX = -0.35 + busCycle * 1.7;
    if (busX > -0.4 && busX < 1.4) {
      const bx = busX * w;
      const by = h * 0.12;
      const bw = w * 0.34;
      const bh = h * 0.2;
      ctx.fillStyle = '#2d3f52';
      ctx.fillRect(bx, by, bw, bh);
      ctx.fillStyle = 'rgba(180, 214, 236, 0.85)';
      for (let i = 0; i < 5; i++) {
        ctx.fillRect(bx + bw * (0.07 + i * 0.18), by + bh * 0.2, bw * 0.12, bh * 0.34);
      }
      ctx.fillStyle = 'rgba(20, 20, 24, 0.35)';
      ctx.fillRect(bx, by + bh, bw, h * 0.02);
    }

    for (const walker of this.walkers) {
      walker.x += walker.vx;
      walker.y += walker.vy;
      if (walker.x < -0.45) walker.x = 1.45;
      if (walker.x > 1.45) walker.x = -0.45;
      if (walker.y < 0.12 || walker.y > 0.9) walker.vy *= -1;

      const px = walker.x * w;
      const py = walker.y * h;
      const r = walker.size * w;
      // Bob, so the stain rule sees per-layer variation rather than a clean sweep.
      const bob = Math.sin(index * 0.55 + walker.y * 30) * r * 0.16;

      ctx.fillStyle = 'rgba(30, 30, 34, 0.32)';
      ctx.beginPath();
      ctx.ellipse(px + r * 1.6, py + r * 0.5, r * 2.1, r * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = walker.accent ?? `rgba(${28 + walker.shade * 90}, ${26 + walker.shade * 86}, ${32 + walker.shade * 96}, 0.95)`;
      ctx.beginPath();
      ctx.ellipse(px, py + bob, r * 0.62, r * 1.15, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(px, py + bob - r * 1.15, r * 0.42, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderInk(index: number, t: number): void {
    const { ctx, width: w, height: h } = this;
    ctx.fillStyle = '#05050a';
    ctx.fillRect(0, 0, w, h);

    const plumeSeed = mulberry32(99);
    const blobs = 150;
    for (let i = 0; i < blobs; i++) {
      const phase = plumeSeed();
      const lane = plumeSeed();
      const speed = 0.35 + plumeSeed() * 0.8;
      const age = (t * speed + phase) % 1;
      const spread = Math.pow(age, 0.7);

      const x = 0.5 + (lane - 0.5) * spread * 1.35 + Math.sin(index * 0.05 + phase * 12) * 0.045 * spread;
      const y = 0.88 - age * 0.95 + Math.cos(index * 0.04 + lane * 9) * 0.03;
      const radius = (0.012 + spread * 0.14) * w;
      const alpha = (1 - age) * 0.5;

      const warm = lane > 0.45;
      const inner = warm ? [255, 168, 88] : [150, 196, 255];
      const outer = warm ? [180, 62, 18] : [40, 78, 170];

      const grad = ctx.createRadialGradient(x * w, y * h, 0, x * w, y * h, radius);
      grad.addColorStop(0, `rgba(${inner[0]}, ${inner[1]}, ${inner[2]}, ${alpha})`);
      grad.addColorStop(0.55, `rgba(${outer[0]}, ${outer[1]}, ${outer[2]}, ${alpha * 0.45})`);
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x * w, y * h, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderPlasma(index: number, _t: number): void {
    const { ctx, width: w, height: h } = this;
    if (!this.imageData) {
      this.imageData = ctx.createImageData(w, h);
    }
    const data = this.imageData.data;
    const time = index * 0.09;

    for (let y = 0; y < h; y++) {
      const fy = y / h;
      for (let x = 0; x < w; x++) {
        const fx = x / w;
        const v =
          Math.sin(fx * 9 + time) +
          Math.sin(fy * 11 - time * 1.3) +
          Math.sin((fx + fy) * 7 + time * 0.7) +
          Math.sin(Math.hypot(fx - 0.5, fy - 0.5) * 22 - time * 2.1);
        const n = (v + 4) / 8;
        const i = (y * w + x) * 4;
        data[i] = Math.max(0, Math.min(255, 255 * Math.pow(n, 1.6)));
        data[i + 1] = Math.max(0, Math.min(255, 255 * Math.pow(n, 2.6) * 0.8));
        data[i + 2] = Math.max(0, Math.min(255, 255 * (1 - Math.pow(n, 1.2)) * 0.9));
        data[i + 3] = 255;
      }
    }
    ctx.putImageData(this.imageData, 0, 0);
  }

  private renderRings(index: number, t: number): void {
    const { ctx, width: w, height: h } = this;
    ctx.fillStyle = '#08080c';
    ctx.fillRect(0, 0, w, h);
    ctx.lineWidth = Math.max(1.5, w * 0.004);

    this.walkers.forEach((origin, i) => {
      const period = 0.22 + origin.shade * 0.3;
      for (let k = 0; k < 4; k++) {
        const age = ((t * 1.6 + k * period + i * 0.17) % 1);
        const radius = age * w * 0.55;
        const alpha = (1 - age) * 0.85;
        ctx.strokeStyle = `hsla(${(i * 47 + index * 1.4) % 360}, 85%, 62%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(origin.x * w, origin.y * h, radius, 0, Math.PI * 2);
        ctx.stroke();
      }
    });
  }
}
